import { useEffect, useState } from 'react';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';

/**
 * Detects the user's system theme and sets it as the default theme
 * apply theme in the next order:
 * 1. Users stored theme on local storage
 * 2. System theme
 * Apply a data-theme attribute to the html tag
 * @returns void
 */
export const DetectAndSetTheme = () => {
  useEffect(() => {
    const storedTheme = localStorage.getItem('theme');
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
      .matches
      ? 'tbitDark'
      : 'tbitLight';

    const theme = storedTheme || systemTheme;
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.classList.remove('tbitLight', 'tbitDark');
    document.documentElement.classList.add(theme);

    // Disable automatic system preference changes if user has manually set a theme
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only apply system theme if user hasn't manually set a preference
      const userTheme = localStorage.getItem('theme');
      if (!userTheme) {
        const newSystemTheme = e.matches ? 'tbitDark' : 'tbitLight';
        document.documentElement.setAttribute('data-theme', newSystemTheme);
        document.documentElement.classList.remove('tbitLight', 'tbitDark');
        document.documentElement.classList.add(newSystemTheme);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);
  return null;
};

export const ChangeTheme = () => {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'tbitDark'
      : 'tbitLight';
  });

  const changeTheme = () => {
    const currentTheme =
      document.documentElement.getAttribute('data-theme') || 'tbitLight';
    const newTheme = currentTheme === 'tbitDark' ? 'tbitLight' : 'tbitDark';

    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    document.documentElement.classList.remove('tbitLight', 'tbitDark');
    document.documentElement.classList.add(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  return (
    <button
      onClick={changeTheme}
      className="btn btn-ghost btn-sm btn-circle hover:bg-base-100 transition-colors"
      title={
        theme === 'tbitDark' ? 'Switch to light mode' : 'Switch to dark mode'
      }
    >
      {theme === 'tbitDark' ? (
        <SunIcon className="h-5 w-5" />
      ) : (
        <MoonIcon className="h-5 w-5" />
      )}
      <span className="sr-only">Toggle dark/light mode</span>
    </button>
  );
};

enum Theme {
  tbitLight = 'tbitLight',
  tbitDark = 'tbitDark',
}

export const UseGetTheme = () => {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('theme') as Theme;
    if (stored && Object.values(Theme).includes(stored)) return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? Theme.tbitDark
      : Theme.tbitLight;
  });

  useEffect(() => {
    const callback = function (
      mutationsList: MutationRecord[],
      observer: MutationObserver,
    ) {
      for (let mutation of mutationsList) {
        if (mutation.type === 'attributes') {
          if (mutation.attributeName === 'data-theme') {
            const newTheme = document.documentElement.getAttribute(
              'data-theme',
            ) as Theme;
            if (newTheme) {
              setTheme(newTheme);
            }
          }
        }
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(document.documentElement, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  return theme;
};

// Hook to check if current theme is dark
export const useIsDarkTheme = () => {
  const theme = UseGetTheme();
  return theme === Theme.tbitDark;
};

// Hook to get theme-aware colors
export const useThemeColors = () => {
  const isDark = useIsDarkTheme();

  return {
    primary: isDark ? '#1fedcc' : '#2c3080',
    secondary: isDark ? '#2c3080' : '#1fedcc',
    accent: '#dac2fe',
    background: isDark ? '#1f2937' : '#ffffff',
    surface: isDark ? '#374151' : '#f8fafc',
    text: isDark ? '#ffffff' : '#1f2937',
    textMuted: isDark ? '#9ca3af' : '#6b7280',
  };
};
